import { HistoryOutlined, RightOutlined, RocketOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Empty, Modal, Tag, Typography, notification } from 'antd'
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useNavigationBlockerContext } from '../../../contexts/navigation-blocker-context'
import { fetchReleaseNotesHistory, fetchUnseenReleaseNotes, markReleaseNotesViewed } from './api'
import { hasDifferentMainBundle } from './build-version'
import { ReleaseNoteItem } from './types'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const VIEWED_EVENT_KEY = 'erp-release-notes-viewed'

interface ReleaseNotesContextValue {
  openHistory: () => void
}

const ReleaseNotesContext = createContext<ReleaseNotesContextValue | null>(null)

const ModalHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 4px 0;
`

const HeadingIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 13px;
  background: #e8f7ee;
  color: #23855b;
  font-size: 21px;
`

const HeadingEyebrow = styled.div`
  color: #668477;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const HeadingTitle = styled.div`
  color: #172b24;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
`

const ModalIntro = styled.div`
  color: #64748b;
  line-height: 1.55;
  margin-bottom: 22px;
`

const ReleaseGroup = styled.section`
  & + & {
    border-top: 1px solid #edf0f3;
    margin-top: 24px;
    padding-top: 24px;
  }
`

const GroupDate = styled.div`
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 14px;
`

const Note = styled.article`
  padding: 0 0 18px 12px;
  border-left: 3px solid #c5e8dc;

  & + & {
    margin-top: 4px;
  }

  h5.ant-typography {
    margin: 8px 0 5px;
  }

  p.ant-typography {
    color: #475569;
    margin-bottom: 4px;
    white-space: pre-line;
  }
`

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(value),
  )

const groupItems = (items: ReleaseNoteItem[]) => {
  const groups = new Map<string, ReleaseNoteItem[]>()
  for (const item of items) {
    const group = groups.get(item.deployment_sha) || []
    group.push(item)
    groups.set(item.deployment_sha, group)
  }
  return [...groups.values()]
}

const fetchManifest = async (): Promise<Record<string, unknown> | null> => {
  const response = await fetch('/asset-manifest.json', { cache: 'no-store' })
  if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) {
    return null
  }
  return response.json()
}

export const useReleaseNotes = () => useContext(ReleaseNotesContext)

export const ReleaseNotesProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { runBlockers } = useNavigationBlockerContext()
  const [mode, setMode] = useState<'unseen' | 'history' | null>(null)
  const [items, setItems] = useState<ReleaseNoteItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [historyError, setHistoryError] = useState(false)
  const manifestRef = useRef<Record<string, unknown> | null>(null)
  const staleBuildRef = useRef(false)
  const checkingRef = useRef(false)
  const modeRef = useRef(mode)
  const suppressedIdsRef = useRef(new Set<string>())
  const historyPageRef = useRef(1)
  modeRef.current = mode

  const showBuildUpdate = useCallback(() => {
    notification.warning({
      key: 'erp-build-update',
      message: 'Доступна новая версия ERP',
      description: (
        <div>
          Сохраните текущие данные, затем обновите страницу. Несохранённые изменения будут потеряны.
          <div style={{ marginTop: 12 }}>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={async () => {
                if (await runBlockers()) window.location.reload()
              }}
            >
              Обновить страницу
            </Button>
          </div>
        </div>
      ),
      placement: 'bottomRight',
      duration: 0,
    })
  }, [runBlockers])

  const checkReleaseNotes = useCallback(async () => {
    if (checkingRef.current || document.hidden || modeRef.current) return
    checkingRef.current = true
    try {
      const manifest = await fetchManifest().catch(() => null)
      if (manifest) {
        if (
          hasDifferentMainBundle(
            manifest,
            [...document.scripts].map(script => script.src),
            window.location.origin,
          ) ||
          (manifestRef.current && JSON.stringify(manifestRef.current) !== JSON.stringify(manifest))
        ) {
          staleBuildRef.current = true
        }
        if (!manifestRef.current) manifestRef.current = manifest
      }
      if (staleBuildRef.current) {
        showBuildUpdate()
        return
      }

      const response = await fetchUnseenReleaseNotes()
      const unseen = response.items.filter(item => !suppressedIdsRef.current.has(item.id))
      if (unseen.length && !modeRef.current) {
        setItems(unseen)
        setHasMore(response.has_more)
        setMode('unseen')
      }
    } catch (error) {
      console.error('Не удалось проверить обновления ERP', error)
    } finally {
      checkingRef.current = false
    }
  }, [showBuildUpdate])

  useEffect(() => {
    void checkReleaseNotes()
    const interval = window.setInterval(() => void checkReleaseNotes(), CHECK_INTERVAL_MS)
    const onFocus = () => void checkReleaseNotes()
    const onVisibilityChange = () => {
      if (!document.hidden) void checkReleaseNotes()
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === VIEWED_EVENT_KEY) {
        modeRef.current = null
        setMode(null)
        void checkReleaseNotes()
      }
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('storage', onStorage)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('storage', onStorage)
      notification.destroy('erp-build-update')
    }
  }, [checkReleaseNotes])

  const openHistory = useCallback(async () => {
    setMode('history')
    setLoading(true)
    setHistoryError(false)
    historyPageRef.current = 1
    try {
      const response = await fetchReleaseNotesHistory(1)
      setItems(response.items)
      setHasMore(response.has_more)
    } catch (error) {
      console.error('Не удалось загрузить историю обновлений ERP', error)
      setHistoryError(true)
      setItems([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return
    setLoading(true)
    try {
      const nextPage = historyPageRef.current + 1
      const response = await fetchReleaseNotesHistory(nextPage)
      historyPageRef.current = nextPage
      setItems(current => [...current, ...response.items])
      setHasMore(response.has_more)
    } catch (error) {
      console.error('Не удалось загрузить историю обновлений ERP', error)
    } finally {
      setLoading(false)
    }
  }, [hasMore, loading])

  const close = useCallback(() => {
    if (modeRef.current === 'unseen') {
      const ids = items.map(item => item.id)
      ids.forEach(id => suppressedIdsRef.current.add(id))
      if (ids.length) {
        void markReleaseNotesViewed(ids)
          .then(() => localStorage.setItem(VIEWED_EVENT_KEY, String(Date.now())))
          .catch(error => console.error('Не удалось отметить обновления просмотренными', error))
      }
    }
    modeRef.current = null
    setMode(null)
    setItems([])
  }, [items])

  const openSection = useCallback(
    async (url: string) => {
      if (!(await runBlockers())) return
      close()
      navigate(url)
    },
    [close, navigate, runBlockers],
  )

  return (
    <ReleaseNotesContext.Provider value={{ openHistory }}>
      {children}
      <Modal
        title={
          <ModalHeading>
            <HeadingIcon>
              {mode === 'history' ? <HistoryOutlined /> : <RocketOutlined />}
            </HeadingIcon>
            <div>
              <HeadingEyebrow>Обновления ERP</HeadingEyebrow>
              <HeadingTitle>{mode === 'history' ? 'История изменений' : 'Что нового'}</HeadingTitle>
            </div>
          </ModalHeading>
        }
        open={mode !== null}
        onOk={close}
        onCancel={close}
        okText={mode === 'history' ? 'Закрыть' : 'Понятно'}
        cancelButtonProps={{ style: { display: 'none' } }}
        maskClosable={false}
        width="min(680px, calc(100vw - 32px))"
        styles={{ body: { maxHeight: 'min(65vh, 640px)', overflowY: 'auto' } }}
      >
        {mode === 'unseen' && (
          <ModalIntro>Собрали изменения, которые могут пригодиться вам в работе.</ModalIntro>
        )}
        {items.length ? (
          groupItems(items).map(group => (
            <ReleaseGroup key={group[0].deployment_sha}>
              <GroupDate>{formatDate(group[0].published_at)}</GroupDate>
              {group.map(item => (
                <Note key={item.id}>
                  <Tag color={item.category === 'feature' ? 'green' : 'blue'}>
                    {item.category === 'feature' ? 'Новое' : 'Исправлено'}
                  </Tag>
                  <Typography.Title level={5}>{item.title}</Typography.Title>
                  <Typography.Paragraph>{item.description}</Typography.Paragraph>
                  {item.url && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        if (item.url) openSection(item.url)
                      }}
                    >
                      Открыть раздел <RightOutlined />
                    </Button>
                  )}
                </Note>
              ))}
            </ReleaseGroup>
          ))
        ) : (
          <Empty
            description={
              loading
                ? 'Загружаем обновления…'
                : historyError
                ? 'Не удалось загрузить историю изменений'
                : 'Пока нет обновлений'
            }
          >
            {historyError && <Button onClick={() => void openHistory()}>Повторить</Button>}
          </Empty>
        )}
        {mode === 'history' && hasMore && (
          <Button onClick={() => void loadMore()} loading={loading} block>
            Показать ещё
          </Button>
        )}
        {mode === 'unseen' && hasMore && (
          <Typography.Text type="secondary">
            Остальные изменения появятся после просмотра этих пунктов.
          </Typography.Text>
        )}
      </Modal>
    </ReleaseNotesContext.Provider>
  )
}
